import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react'
import {
  ReferenceInput,
  TextInput,
  useDataProvider,
} from 'react-admin'
import { Box } from '@material-ui/core'
import get from 'lodash/get'
import { Autocomplete, DateInput, NumberInput } from '../../custom'
import {
  referenceInputOptions,
  formInputOptions,
} from '../../util/component-options'
import { useFormStyles } from '../../../styles'
import {
  useLabel,
  useTranslateResource,
} from '../../../hooks'
import { relationships } from '../../../data-model'
import { Form, TruckDriverFields, getValidator } from '../../form'
import {
  useTonnageRateFields,
  useTonnageRateReferenceFields,
  useAutofillFields,
} from '../../form/functions'

const validate = getValidator()

const skipTabInputProps = {
  tabIndex: '-1',
}

const FormBody = props => {
  const { onRateChange, ...restProps } = props
  const { resource, record } = restProps
  const extraction = record.tonnage_ticket_extraction
  const isNewRecord = !record.id || Boolean(extraction)
  const resourceRelationships = relationships[resource]
  const formClasses = useFormStyles(restProps)
  const dataProvider = useDataProvider()
  const [impliedRate, setImpliedRate] = useState()
  const rateFields = useTonnageRateFields()
  const rateLookupReferenceFields = useTonnageRateReferenceFields()
  const label = useLabel(restProps)
  const translate = useTranslateResource(resource, 'form')

  const autofillRateFields = useMemo(() => {
    return rateFields.map(f => f.source)
  }, [rateFields])

  const rate = useMemo(() => {
    return impliedRate
  }, [impliedRate])

  const shouldAutofillRateField = useCallback((autofillSource, value) => {
    return isNewRecord && Boolean(value)
  }, [isNewRecord])

  const shouldAutofillDriverField = useCallback(() => {
    return isNewRecord
  }, [isNewRecord])

   const [
    getRateFieldIsAutofilled,
    onAutofillRateFieldChange,
   ]= useAutofillFields(autofillRateFields, rate, shouldAutofillRateField)

  const [rateReferenceFieldValues, setRateReferenceValues] = useState(() => {
    const { record } = props
    return rateLookupReferenceFields.reduce((accum, { source }) => {
      return {
        ...accum,
        [source]: get(record, [source, 'id']),
      }
    }, {})
  })

  const onRateLookupFieldChange = useCallback((field, value) => {
    setRateReferenceValues(prev => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const getRateFieldHelperText = useCallback(field => {
    const value = get(rate, field)
    const autofilled = getRateFieldIsAutofilled(field)

    if (isNewRecord) {
      if (rate) {
        if (autofilled) {
          return translate('general.autofilledValueFromRate', { value })
        } else {
          if (value == null) {
            return translate('general.autofilledBlankValueFromRate')
          } else {
            return translate('general.currentRateValue', { value })
          }
        }
      } else {
        return translate('general.noRate', { value })
      }
    } else {
      if (rate) {
        if (value == null) {
          return translate('general.autofilledBlankValueFromRate')
        } else {
          return translate('general.currentRateValue', { value })
        }
      } else {
        return translate('general.noRate', { value })
      }
    }
  }, [rate, isNewRecord, translate, getRateFieldIsAutofilled])

  const getExtractedValueHelperText = useCallback(field => {
    if (extraction) {
      const value = extraction[field]
      if (value) {
        return translate('edit.extractedText', { value })
      }
    }
  }, [extraction, translate])

  const getRateReferenceFieldHelperText = useCallback(field => {
    const texts = []
    texts.push(getExtractedValueHelperText(field.replace(/_id$/, '')))
    if (rate) {
      const value = get(rate, [field, 'name'])
      if (value) {
        texts.push(translate('general.currentRateValue', { value }))
      }
    }
    const result = texts.filter(Boolean).join('\n')
    return result.length ? result : null
  }, [rate, translate, getExtractedValueHelperText])

  const validateTonnage = useMemo(() => {
    const max = 35
    return getValidator(true, [
      value => value > max ? translate('validate.tonnageTooHigh', { max }) : null,
    ])
  }, [translate])

  useEffect(() => {
    if (Object.values(rateReferenceFieldValues).every(v => !v)) {
      setImpliedRate()
      return
    }
    (async () => {
      const filter = Object.entries(rateReferenceFieldValues).reduce((accum, [field, value]) => {
        return value ? {
          ...accum,
          [`${field}_id_eq`]: value,
        } :
        {
          ...accum,
          [`${field}_id_null`]: true,
        }
      }, {})
      const responseData = await dataProvider.getList('tonnage_rates', { filter })
      setImpliedRate(responseData.data[0])
    })()
  }, [rateReferenceFieldValues, dataProvider])

  useEffect(() => {
    onRateChange(rate)
  }, [rate, onRateChange])

  return (
    <Box className={formClasses.grid}>
      <Box>
        {
          rateLookupReferenceFields.map(({ source, required, optionText, optionSubtext, label: _label }, idx) => {
            const sourceWithId = `${source}.id`
            return (
              <ReferenceInput
                {...formInputOptions}
                {...referenceInputOptions(resourceRelationships[source])}
                key={source}
                source={sourceWithId}
                reference={resourceRelationships[source]}
                label={label(_label)}
                onChange={onRateLookupFieldChange.bind(null, source)}
                required={required}
                autoFocus={idx === 0}
                inputProps={{
                  ...formInputOptions.inputProps,
                }}
              >
                <Autocomplete
                  validate={getValidator(required)}
                  optionText={optionText}
                  optionSubtext={optionSubtext}
                  helperText={getRateReferenceFieldHelperText(source)}
                />
              </ReferenceInput>
            )
          })
        }
      </Box>

      <Box>
        <TextInput
          {...formInputOptions}
          source='ticket_number'
          label={label('ticket_number')}
          validate={validate}
          required
        />

        <DateInput
          {...formInputOptions}
          validate={validate}
          source='worked_at_date'
          label={label('worked_at_date')}
          required
        />

        <NumberInput
          {...formInputOptions}
          source='net_weight_in_tons'
          label={label('net_weight_in_tons')}
          step={0.01}
          min={0}
          validate={validateTonnage}
          required
        />

        {
          rateFields.map(({ source, required, label: _label }) => {
            return (
              <NumberInput
                {...formInputOptions}
                validate={getValidator(required)}
                key={source}
                source={source}
                label={label(_label)}
                step={0.01}
                min={0}
                required={required}
                onChange={onAutofillRateFieldChange.bind(null, source)}
                helperText={getRateFieldHelperText(source)}
                inputProps={{
                  ...formInputOptions.inputProps,
                  ...(getRateFieldIsAutofilled(source) ? skipTabInputProps : undefined),
                }}
              />
            )
          })
        }
      </Box>

      <Box>
        <TruckDriverFields
          {...restProps}
          truckHelperText={getExtractedValueHelperText('truck_number')}
          shouldAutofill={shouldAutofillDriverField}
        />
      </Box>
    </Box>
  )
}

// wrap component so we can use useForm hook
const TonnageTicketsForm = props => {
  const { resource, record } = props
  const [rate, setRate] = useState()
  const translate = useTranslateResource(resource, 'form')
  const isDisabled = Boolean(record.snapshot_mode)

  // Submit current tonnage_rate
  const transform = useCallback((data) => {
    return {
      ...data,
      tonnage_rate: {
        id: rate?.id,
      },
    }
  }, [rate])

  const getOptimisticFailureMessageContext = useCallback((data) => {
    return `#${data.ticket_number}`
  }, [])

  return (
    <Form
      {...props}
      infoText={translate('info.formHelper')}
      transform={transform}
      useOptimisticSave
      getOptimisticFailureMessageContext={getOptimisticFailureMessageContext}
      isUpdateDisabled={isDisabled}
      isDeleteDisabled={isDisabled}
    >
      <FormBody
        {...props}
        onRateChange={setRate}
      />
    </Form>
  )
}

export default TonnageTicketsForm
